"use client";

import { formatDistanceToNowStrict } from "date-fns";
import { Columns3, MapPin, RefreshCw } from "lucide-react";
import { useRef, useState } from "react";

import { useHasMounted } from "@/hooks/useHasMounted";

import { useEstimator } from "../../_context/EstimatorContext";
import { useComparisonRows } from "../../_hooks/useComparisonRows";
import { useElementWidth } from "../../_hooks/useElementWidth";
import {
  ROUTE_ROWS_SHOWN,
  TABLE_DENSE_COLUMN_PX,
  TABLE_LABEL_COMPACT_PX,
  TABLE_LABEL_SNUG_PX,
  TABLE_SNUG_PX,
  TABLE_ROOMY_PX,
  TABLE_LABEL_PX,
  TABLE_LABEL_WIDE_PX,
  SUMMARY_MIN_CITIES,
  COMPARE_MAX_CITIES,
  MAX_DESTINATIONS,
  ADD_CITY_COLUMN_PX,
  TABLE_MIN_CITY_PX,
} from "../../_logic/constants";
import { OpenCityProvider } from "../../_context/OpenCityContext";
import { CityFlightsPanel } from "../flights/CityFlightsPanel";
import { CitySummaryList } from "./CitySummaryList";
import { ViewToggle, type ComparisonView } from "./ViewToggle";
import { PricingNotices } from "./PricingNotices";
import { CityCostBars } from "./CityCostBars";
import { ComparisonGrid } from "./ComparisonGrid";

/**
 * The per-city comparison: flights, hotel, meeting space, meals, per person and total. The
 * cheapest city is tagged, missing fares are called out instead of counted as zero, and cells
 * that depend on a missing input show a dash under one prompt saying what to add.
 * @returns The comparison section, or an empty state before any city is added.
 * @throws Never.
 */
export const ComparisonTable = () => {
  // Hooks
  const { workspace, estimate, money, flights, refreshHotels, costView: mode, setCostView: setMode } = useEstimator();
  const hasMounted = useHasMounted();
  const { ref, width } = useElementWidth<HTMLElement>();
  const section = useRef<HTMLElement | null>(null);

  // States
  const [showAllRoutes, setShowAllRoutes] = useState(false);
  const [detailCity, setDetailCity] = useState<string | null>(null);
  /**
   * The planner's own choice of view. It holds only while the city count stays on the same side of
   * the table/list boundary; crossing it goes back to the automatic view.
   */
  const [chosen, setChosen] = useState<{ view: ComparisonView; picked: string[]; many: boolean } | null>(null);
  /** Cities picked in the list for the side-by-side table. */
  const [pickedNames, setPickedNames] = useState<string[]>([]);

  // Derived
  const { cities: allCities, headcount, lowestCity } = estimate;
  // A city removed from the trip drops out of the selection too.
  const picked = pickedNames.filter((name) => allCities.some((city) => city.city === name));
  // Up to three cities compare side by side; four or more open as the ranked list.
  const many = allCities.length >= SUMMARY_MIN_CITIES;
  const override = chosen && chosen.many === many ? chosen : null;
  const comparing = override?.view === "table" ? allCities.filter((city) => override.picked.includes(city.city)) : [];
  const summary = override ? override.view === "list" : many;
  const view: ComparisonView = summary ? "list" : "table";
  const cities = comparing.length >= 2 ? comparing : allCities;
  const showView = (next: ComparisonView) =>
    switchView(next === (many ? "list" : "table") ? null : { view: next, picked, many });
  const switchView = (next: typeof chosen) => {
    setChosen(next);
    // Keep the comparison's top in view when the shorter view replaces the longer one.
    if ((section.current?.getBoundingClientRect().top ?? 0) < 0) section.current?.scrollIntoView({ block: "start" });
  };
  const totalMode = mode === "total" && headcount > 0;

  // The toggle for the remaining home cities sits under the flights it reveals, in the table and
  // in each open breakdown, so it always changes something on screen.
  const rows = useComparisonRows(totalMode, {
    limit: ROUTE_ROWS_SHOWN,
    showAll: showAllRoutes,
    onToggle: () => setShowAllRoutes((current) => !current),
  });
  // Narrower columns get a narrower label column; past that the table scrolls sideways.
  const labelPx =
    width > 0 && width < TABLE_SNUG_PX
      ? TABLE_LABEL_SNUG_PX
      : width > 0 && width < TABLE_ROOMY_PX
        ? TABLE_LABEL_COMPACT_PX
        : cities.length <= 2
          ? TABLE_LABEL_WIDE_PX
          : TABLE_LABEL_PX;
  // Room for another city, and not a picked few from the list, where a new one would land outside.
  // Only where the column fits beside the cities: it never pushes the table into scrolling sideways.
  const addCity =
    cities === allCities &&
    workspace.destinations.length < MAX_DESTINATIONS &&
    (width === 0 || width - labelPx - ADD_CITY_COLUMN_PX >= cities.length * TABLE_MIN_CITY_PX);
  // Relative to the clock, so only after mount: a server render would disagree with the client.
  // With no home cities there are no fares, so no "checked" time either.
  const checkedAgo =
    !flights.checkedAt || !hasMounted || workspace.origins.length === 0
      ? null
      : Date.now() - flights.checkedAt.getTime() < 60_000
        ? "just now"
        : formatDistanceToNowStrict(flights.checkedAt, { addSuffix: true });

  // Render
  if (cities.length === 0) {
    return (
      <section className="flex flex-col items-center gap-2 rounded-2xl border border-dashed border-gray-200 px-6 py-8 text-center">
        <span className="flex size-10 items-center justify-center rounded-full bg-gray-100">
          <MapPin className="size-5 text-gray-500" />
        </span>
        <h2 className="text-base font-semibold text-gray-900">Add a city to compare</h2>
        <p className="max-w-md text-sm font-medium text-gray-500">
          Add up to five cities under Where, or ask Roo. Each gets live fares, hotel ranges and a per-person total.
        </p>
      </section>
    );
  }

  return (
    <section
      ref={(element) => {
        ref(element);
        section.current = element;
      }}
      aria-label="Comparison"
      tabIndex={-1}
      className="flex flex-col gap-3 outline-none @container"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-base font-semibold tracking-tight text-gray-900">
          {totalMode ? `Total for ${headcount}` : "Cost per person"}
        </h2>
        <div className="flex flex-wrap items-center gap-2">
          {allCities.length > 1 && <ViewToggle value={view} onChange={showView} />}
          {headcount > 0 && workspace.dates && (
            <div
              role="group"
              aria-label="Show costs"
              data-tour="estimator-cost-mode"
              className="relative inline-flex rounded-full border border-gray-200 p-0.5"
            >
              {(["perPerson", "total"] as const).map((option) => (
                // ui-lint-disable-next-line raw-control: a custom control (segment, chip, calendar day, stepper, icon or inline editor) styled to its row; FetchButton's sizes and 3D treatment don't fit
                <button
                  key={option}
                  type="button"
                  aria-pressed={mode === option}
                  disabled={option === "total" && headcount === 0}
                  onClick={() => setMode(option)}
                  // The highlight follows the same state as the title and the numbers, in the same frame.
                  className="relative rounded-full px-3 py-1 text-xs font-medium text-gray-500 transition-colors hover:text-gray-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 disabled:cursor-not-allowed disabled:text-gray-300 aria-pressed:bg-gray-100 aria-pressed:text-gray-900"
                >
                  <span className="relative">{option === "perPerson" ? "Per person" : `Total for ${headcount}`}</span>
                </button>
              ))}
            </div>
          )}
          {summary && (
            // ui-lint-disable-next-line raw-control: a custom control (segment, chip, calendar day, stepper, icon or inline editor) styled to its row; FetchButton's sizes and 3D treatment don't fit
            <button
              type="button"
              // aria-disabled rather than disabled, so the hint still shows on hover.
              aria-disabled={picked.length < 2}
              title={picked.length === 1 ? "Select one more" : picked.length === 0 ? "Select 2–3 cities" : undefined}
              onClick={() => picked.length >= 2 && switchView({ view: "table", picked, many })}
              className={
                picked.length >= 2
                  ? "inline-flex h-[30px] items-center gap-1.5 rounded-full bg-indigo-600 px-3 text-xs font-medium text-white transition-colors hover:bg-indigo-700 active:bg-indigo-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2"
                  : // ui-lint-disable-next-line low-contrast-text: an inactive control; WCAG 1.4.3 exempts disabled text
                    "inline-flex h-[30px] cursor-not-allowed items-center gap-1.5 rounded-full border border-gray-200 bg-white px-3 text-xs font-medium text-gray-400"
              }
            >
              <Columns3 aria-hidden className="size-3.5" />
              {picked.length >= 2 ? `Compare ${picked.length}` : "Compare"}
            </button>
          )}
        </div>
      </div>

      <PricingNotices />

      <OpenCityProvider value={setDetailCity}>
        {/* The list and the table swap in one frame, so no frame shows neither. */}
        <div key={summary ? "list" : "table"}>
          {summary ? (
            <CitySummaryList
              cities={allCities}
              lowestCity={lowestCity}
              byTotal={totalMode}
              picked={picked}
              onTogglePick={(city) =>
                setPickedNames((current) =>
                  current.includes(city)
                    ? current.filter((name) => name !== city)
                    : current.length >= COMPARE_MAX_CITIES
                      ? current
                      : [...current, city]
                )
              }
              renderDetail={(city) => (
                <>
                  <CityCostBars city={city} totalMode={totalMode} />
                  <ComparisonGrid
                    cities={[city]}
                    rows={rows}
                    lowestCity={null}
                    layout={{ labelPx, dense: false, bare: true }}
                    onOpenCity={setDetailCity}
                  />
                </>
              )}
            />
          ) : (
            // One column per city; more than fit scroll sideways. No card layout at any width.
            <ComparisonGrid
              cities={cities}
              rows={rows}
              lowestCity={lowestCity}
              layout={{
                labelPx,
                dense:
                  width > 0 &&
                  (width - labelPx - (addCity ? ADD_CITY_COLUMN_PX : 0)) / cities.length < TABLE_DENSE_COLUMN_PX,
                addCity,
              }}
              onOpenCity={setDetailCity}
            />
          )}
        </div>
      </OpenCityProvider>

      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <p aria-live="polite" className="text-xs font-medium text-gray-600">
          {[
            flights.status === "searching" && workspace.dates
              ? `Searching flights${flights.progress > 0 ? ` · ${Math.round(flights.progress * 100)}%` : ""}`
              : flights.status === "error"
                ? `${flights.entry?.error ?? "The flight search failed"}. Fares shown may be incomplete`
                : checkedAgo
                  ? `Fares checked ${checkedAgo}`
                  : null,
            money.fxNote,
            cities.some((city) => city.flights.missing.length > 0) ? "Missing fares are left out of the average" : null,
          ]
            .filter(Boolean)
            .join(" · ")}
        </p>
        {flights.status !== "searching" && flights.status !== "idle" && workspace.origins.length > 0 && (
          // ui-lint-disable-next-line raw-control: a custom control (segment, chip, calendar day, stepper, icon or inline editor) styled to its row; FetchButton's sizes and 3D treatment don't fit
          <button
            type="button"
            onClick={() => {
              flights.refresh();
              refreshHotels();
            }}
            className="inline-flex items-center gap-1 rounded px-0.5 text-xs font-medium text-gray-600 underline underline-offset-2 hover:text-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
          >
            <RefreshCw aria-hidden className="size-3" />
            {flights.status === "error" ? "Retry fare search" : "Refresh fares"}
          </button>
        )}
      </div>
      <CityFlightsPanel city={detailCity} onClose={() => setDetailCity(null)} />
    </section>
  );
};
